import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Eye, ArrowRight, Newspaper, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';

/**
 * LatestBlogCarousel - Displays latest blog posts in an elegant carousel
 * Shows 3 posts at a time, auto-advances
 */
const LatestBlogCarousel = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const POSTS_PER_PAGE = 3;
  const MAX_POSTS = 9;

  useEffect(() => {
    fetchLatestPosts();
  }, []);

  const fetchLatestPosts = async () => {
    try {
      const response = await api.get(`/api/blog?published_only=true&limit=${MAX_POSTS}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carousel navigation
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  // Auto-advance carousel every 8 seconds
  useEffect(() => {
    if (posts.length <= POSTS_PER_PAGE) return;
    
    const interval = setInterval(nextSlide, 8000);
    return () => clearInterval(interval);
  }, [posts.length, nextSlide]);

  // Get current visible posts
  const visiblePosts = posts.slice(
    currentIndex * POSTS_PER_PAGE,
    currentIndex * POSTS_PER_PAGE + POSTS_PER_PAGE
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Publicação Científica': 'bg-blue-500 text-white',
      'Metodologia': 'bg-purple-500 text-white',
      'QUALIS/CAPES': 'bg-emerald-500 text-white',
      'Dicas de Escrita': 'bg-amber-500 text-white',
      'Eventos Acadêmicos': 'bg-rose-500 text-white',
      'Novidades': 'bg-cyan-500 text-white',
      'Tutoriais': 'bg-indigo-500 text-white',
    };
    return colors[category] || 'bg-gray-500 text-white';
  };

  // Strip HTML tags for excerpt
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#002FA7]/10 text-[#002FA7] px-4 py-2 rounded-full mb-4">
            <Newspaper className="h-4 w-4" />
            <span className="text-sm font-medium">Blog</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-3">
            Artigos e Dicas
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Conteúdos exclusivos para ajudar você a publicar em revistas científicas de alto impacto
          </p>
        </div>

        {/* Carousel container */}
        <div className="relative">
          {/* Navigation arrows */}
          {posts.length > POSTS_PER_PAGE && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors border border-gray-200 hidden md:flex items-center justify-center"
                aria-label="Anterior"
              >
                <ChevronLeft className="h-6 w-6 text-[#002FA7]" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors border border-gray-200 hidden md:flex items-center justify-center"
                aria-label="Próximo"
              >
                <ChevronRight className="h-6 w-6 text-[#002FA7]" />
              </button>
            </>
          )}

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
            {visiblePosts.map((post) => (
              <article
                key={post.post_id}
                onClick={() => navigate(`/blog/${post.slug || post.post_id}`)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:border-[#002FA7]/20 transition-all duration-300 cursor-pointer group flex flex-col"
                data-testid={`blog-card-${post.post_id}`}
              >
                {/* Cover Image */}
                {post.cover_image ? (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.cover_image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-[#002FA7] to-[#0050FF] flex items-center justify-center">
                    <Newspaper className="h-16 w-16 text-white/30" />
                  </div>
                )}

                {/* Card body */}
                <div className="p-5 flex-1 flex flex-col">
                  {/* Category and Featured badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`${getCategoryColor(post.category)} text-xs font-medium`}>
                      {post.category}
                    </Badge>
                    {post.featured && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        Destaque
                      </Badge>
                    )}
                  </div>

                  <h3 className="font-bold text-lg text-[#0A0A0A] mb-2 line-clamp-2 group-hover:text-[#002FA7] transition-colors">
                    {post.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-1">
                    {post.excerpt || stripHtml(post.content).substring(0, 120) + '...'}
                  </p>

                  {/* Meta info */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{formatDate(post.published_at || post.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{post.views || 0} views</span>
                    </div>
                  </div>
                </div>

                {/* Card footer */}
                <div className="px-5 pb-4">
                  <div className="flex items-center justify-between text-sm text-[#002FA7] font-medium group-hover:text-[#001f75]">
                    <span>Ler artigo</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Dots indicator */}
          {posts.length > POSTS_PER_PAGE && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-8 bg-[#002FA7]' 
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Ir para página ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Mobile navigation */}
          {posts.length > POSTS_PER_PAGE && (
            <div className="flex justify-center gap-4 mt-6 md:hidden">
              <Button
                variant="outline"
                size="icon"
                onClick={prevSlide}
                className="rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={nextSlide}
                className="rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10">
          <Button
            onClick={() => navigate('/blog')}
            variant="outline"
            className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-8 py-6 text-lg rounded-full transition-all"
            data-testid="blog-view-all-btn"
          >
            Ver todos os artigos
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LatestBlogCarousel;
